import { useReducer, useCallback } from 'react';
import { NormalizedContentAssistanceResult } from '../api/contentAssistanceApi';
import { FINISHED } from '../hooks/constants';
import { Action } from '../utils/types';

const SHOW_RESULT = 'SHOW_RESULT';
const INCREMENT_CLICK_COUNT = 'INCREMENT_CLICK_COUNT';
const SET_GENERATED_CONTENT = 'SET_GENERATED_CONTENT';
const SET_INPUT = 'SET_INPUT';

type InlineAutocompleteMenuState = {
  regenerateClickCount: number;
  generationInput: string;
  generatedContent?: NormalizedContentAssistanceResult | null;
  allResults: NormalizedContentAssistanceResult[];
  selectedResultIndex: number;
};

const initialState: InlineAutocompleteMenuState = {
  regenerateClickCount: 0, // max clicks is 5
  generationInput: '',
  generatedContent: null,
  allResults: [],
  selectedResultIndex: 0,
};

type GenerationInputPayload = string;

type FinishedPayload = {
  response: NormalizedContentAssistanceResult[];
  generatedContent: NormalizedContentAssistanceResult;
};

type GeneratedContentPayload = {
  generatedContent: NormalizedContentAssistanceResult;
};

type ShowResultPayload = {
  selectedResultIndex: number;
};

type InlineAutocompleteMenuAction =
  | Action<typeof SHOW_RESULT, ShowResultPayload>
  | Action<typeof INCREMENT_CLICK_COUNT>
  | Action<typeof SET_INPUT, GenerationInputPayload>
  | Action<typeof SET_GENERATED_CONTENT, GeneratedContentPayload>
  | Action<typeof FINISHED, FinishedPayload>;

const reducer = (
  state: InlineAutocompleteMenuState,
  action: InlineAutocompleteMenuAction
): InlineAutocompleteMenuState => {
  const { type, payload } = action;
  switch (type) {
    case SET_INPUT:
      return {
        ...state,
        generationInput: payload,
      };
    case INCREMENT_CLICK_COUNT:
      return {
        ...state,
        regenerateClickCount: state.regenerateClickCount + 1,
      };
    case FINISHED:
      return {
        ...state,
        allResults: [...state.allResults, ...payload.response],
        // Reset index, and content shown in preview
        selectedResultIndex:
          state.allResults.length + payload.response.length - 1,
        generatedContent: payload.generatedContent,
      };
    case SET_GENERATED_CONTENT:
      return {
        ...state,
        generatedContent: payload.generatedContent,
      };
    case SHOW_RESULT:
      return {
        ...state,
        selectedResultIndex: payload.selectedResultIndex,
        generatedContent: state.allResults[payload.selectedResultIndex],
      };
    default:
      throw new Error('Unexpected action type');
  }
};
const useInlineAutocompleteMenuState = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setGeneratedContent = useCallback(
    (generatedContent: NormalizedContentAssistanceResult) => {
      dispatch({
        type: SET_GENERATED_CONTENT,
        payload: {
          generatedContent,
        },
      });
    },
    []
  );

  const setInput = useCallback((value: string) => {
    dispatch({
      type: SET_INPUT,
      payload: value,
    });
  }, []);

  const onSuccess = useCallback(
    (response: NormalizedContentAssistanceResult[]) =>
      dispatch({
        type: FINISHED,
        payload: { response, generatedContent: response[0] },
      }),
    []
  );

  const incrementCount = useCallback(
    () =>
      dispatch({
        type: INCREMENT_CLICK_COUNT,
      }),
    []
  );

  const setShowResult = useCallback(
    (selectedResultIndex: number) =>
      dispatch({ type: SHOW_RESULT, payload: { selectedResultIndex } }),
    []
  );
  return {
    state,
    actions: {
      onSuccess,
      setGeneratedContent,
      incrementCount,
      setShowResult,
      setInput,
    },
  };
};

export default useInlineAutocompleteMenuState;
